import React, { createContext, useContext, useEffect, useState, ReactNode, useCallback } from 'react';
import type { User } from '@shared/types/user';
import type { Domain } from '@/types/navigation';
import { markSessionExpired, clearLastLocation } from '../utils/sessionContinuity';
import { resolveUserDomain } from '../utils/resolveUserDomain';
import io, { Socket } from 'socket.io-client';

// ============================================
// AUTH CONTEXT TYPES
// ============================================

interface AuthState {
  user: User | null;
  isAuthenticated: boolean;
  isLoading: boolean;
  socket?: Socket | null;
}

interface AuthContextType extends AuthState {
  login: (email: string, password: string) => Promise<void>;
  logout: () => Promise<void>;
  hasPermission: (permission: string) => boolean;
  userDomain: Domain;
}

// ============================================
// AUTH CONTEXT
// ============================================

const AuthContext = createContext<AuthContextType | undefined>(undefined);

export const AuthProvider: React.FC<{ children: ReactNode }> = ({ children }) => {
  const [state, setState] = useState<AuthState>({
    user: null,
    isAuthenticated: false,
    isLoading: true,
    socket: null
  });

  // ============================================
  // PHASE-1 RBAC: PERMISSION CHECKING
  // ============================================
  // ✅ STRICT: No fallback, no role magic, no legacy logic
  // ✅ Permissions are the ONLY source of truth (from backend)
  // ✅ If permissions is missing or empty → access is denied

  const hasPermission = (permission: string): boolean => {
    if (!state.user || !Array.isArray(state.user.permissions)) {
      return false;
    }

    // Phase-1 RBAC: Check if user has the permission directly
    return state.user.permissions.includes(permission);
  };

  // ============================================
  // DOMAIN DETERMINATION (PRBA - Permission-Only RBAC)
  // ============================================
  // ✅ STRICT: Domain is determined by dashboard permission AND company context
  // ✅ No fallback, no role-based domain assignment
  // ✅ Each user belongs to exactly one domain
  // ✅ Auditors are distinguished by companyId (External = NULL, Internal = NOT NULL)

  const userDomain = resolveUserDomain(state.user);

  // ============================================
  // WEBSOCKET CONNECTION
  // ============================================

  // Socket.IO connection is intentionally disabled: the backend uses SSE
  // (sseRegistry) for push, not Socket.IO. Leaving the client connecting
  // to /socket.io/ causes a 502 Bad Gateway retry storm in the browser
  // and never receives any data. Real-time updates flow through SSE and
  // the window `changeNotificationReceived` event dispatchers elsewhere.
  const connectSocket = useCallback(() => {
    // no-op
  }, []);

  const disconnectSocket = useCallback(() => {
    if (state.socket) {
      state.socket.disconnect();
      setState(prev => ({ ...prev, socket: null }));
    }
  }, [state.socket]);

  // ============================================
  // AUTHENTICATION
  // ============================================

  const login = useCallback(async (email: string, password: string) => {
    setState(prevState => ({ ...prevState, isLoading: true }));

    try {
      const response = await fetch('/api/auth/login', {
        method: 'POST',
        credentials: 'include', // needed to store httpOnly cookies from the response
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({ email, password }),
      });

      // Handle non-JSON responses (like rate limit errors)
      const contentType = response.headers.get('content-type');
      let result: any;
      
      if (contentType && contentType.includes('application/json')) {
        result = await response.json();
      } else {
        // For rate limit errors that return plain text
        const text = await response.text();
        try {
          result = JSON.parse(text);
        } catch {
          // If it's not JSON, create an error object
          result = {
            success: false,
            message: text || 'Login failed. Please try again later.'
          };
        }
      }

      if (result.success && result.requiresTwoFactor) {
        // 2FA code sent — store temp token for verification page
        setState(prevState => ({ ...prevState, isLoading: false }));
        sessionStorage.setItem('2fa_temp_token', result.tempToken || '');
        sessionStorage.setItem('2fa_email', email);
        const twoFaError = new Error(result.message || 'Two-factor authentication required');
        (twoFaError as any).requiresTwoFactor = true;
        throw twoFaError;
      } else if (result.success && result.user) {
        const user = result.user;

        import.meta.env.DEV && console.log('[AuthContext] Login successful, user data:', {
          id: user.id,
          email: user.email,
          roles: user.roles,
          permissions: user.permissions,
          permissionsCount: user.permissions?.length || 0
        });

        // Tokens stored in both httpOnly cookies (server-set, XSS-safe primary auth)
        // and localStorage (fallback for EventSource/SSE and session persistence).
        localStorage.setItem('token', result.token || '');
        localStorage.setItem('refreshToken', result.refreshToken || '');
        localStorage.setItem('user', JSON.stringify(user));

        setState(prev => ({
          ...prev,
          user,
          isAuthenticated: true,
          isLoading: false
        }));

        // Connect WebSocket after login
        connectSocket();

        // Navigation will be handled by the calling component
      } else if (result.requiresTwoFactorSetup) {
        // System admin had 2FA auto-enabled — treat same as requiresTwoFactor
        setState(prevState => ({ ...prevState, isLoading: false }));
        sessionStorage.setItem('2fa_temp_token', result.tempToken || '');
        sessionStorage.setItem('2fa_email', email);
        const setupError = new Error(result.message || 'Two-factor authentication has been enabled. Check your email for a verification code.');
        (setupError as any).requiresTwoFactor = true;
        throw setupError;
      } else {
        console.error('[AuthContext] Login failed:', result.message);
        setState(prevState => ({ ...prevState, isLoading: false }));
        throw new Error(result.message || 'Login failed');
      }
    } catch (error: any) {
      console.error('[AuthContext] Login error:', error);
      setState(prevState => ({ ...prevState, isLoading: false }));
      throw error;
    }
  }, []);

  const logout = useCallback(async () => {
    // Check if this is an impersonation session
    const impersonationToken = sessionStorage.getItem('impersonation_token');
    if (impersonationToken) {
      // For impersonation sessions, close the tab
      sessionStorage.removeItem('impersonation_user');
      sessionStorage.removeItem('impersonation_token');
      setState(prev => ({
        ...prev,
        user: null,
        isAuthenticated: false,
        isLoading: false
      }));
      // Close the impersonation tab
      window.close();
      return;
    }
    setState(prevState => ({ ...prevState, isLoading: true }));

    try {
      const token = localStorage.getItem('token');
      if (token) {
        // Try to notify server, but don't fail if token is invalid
        try {
          const csrfToken = document.cookie.match(/(?:^|;\s*)csrf_token=([^;]+)/)?.[1] ?? '';
          await fetch('/api/auth/logout', {
            method: 'POST',
            credentials: 'include', // send cookies so server can clear them
            headers: {
              Authorization: `Bearer ${token}`,
              'Content-Type': 'application/json',
              'X-CSRF-Token': csrfToken,
            }
          });
        } catch (apiError) {
          console.warn('[AuthContext] Logout API call failed, proceeding with local logout:', apiError);
        }
      }

      // Clear session continuity data on explicit logout
      clearLastLocation();

      localStorage.removeItem('token');
      localStorage.removeItem('refreshToken');
      localStorage.removeItem('user');

      // Disconnect WebSocket before logout
      disconnectSocket();

      setState(prev => ({
        ...prev,
        user: null,
        isAuthenticated: false,
        isLoading: false
      }));

      // Reload the page to ensure clean state
      window.location.reload();
    } catch (error) {
      console.error('[AuthContext] Logout error:', error);
      // Even on error, clear state and reload
      localStorage.removeItem('token');
      localStorage.removeItem('refreshToken');
      localStorage.removeItem('user');
      setState({
        user: null,
        isAuthenticated: false,
        isLoading: false
      });
      window.location.reload();
      throw error;
    }
  }, []);

  // ============================================
  // IMPERSONATION LOGIN
  // ============================================

  const impersonationLogin = useCallback(async (impersonationToken: string) => {
    setState(prevState => ({ ...prevState, isLoading: true }));

    try {
      // Use the impersonation token directly for authentication
      const response = await fetch('/api/auth/verify-impersonation', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({ token: impersonationToken }),
      });

      const result = await response.json();

      if (result.success && result.user) {
        const user = result.user;

        import.meta.env.DEV && console.log('[AuthContext] Impersonation login successful, user data:', {
          id: user.id,
          email: user.email,
          roles: user.roles,
          permissions: user.permissions,
          permissionsCount: user.permissions?.length || 0,
          isImpersonation: user.isImpersonation
        });

        // Store impersonation token separately (use sessionStorage to avoid affecting other tabs)
        sessionStorage.setItem('impersonation_token', impersonationToken);
        sessionStorage.setItem('impersonation_user', JSON.stringify(user));

        setState({
          user,
          isAuthenticated: true,
          isLoading: false
        });

        // Clean up URL
        const url = new URL(window.location.href);
        url.searchParams.delete('impersonation_token');
        window.history.replaceState({}, '', url.toString());
      } else {
        console.error('[AuthContext] Impersonation login failed:', result.message);
        setState(prevState => ({ ...prevState, isLoading: false }));
        throw new Error(result.message || 'Impersonation login failed');
      }
    } catch (error: any) {
      console.error('[AuthContext] Impersonation login error:', error);
      setState(prevState => ({ ...prevState, isLoading: false }));
      throw error;
    }
  }, []);

  // ============================================
  // INITIALIZE FROM LOCAL STORAGE OR URL
  // ============================================

  useEffect(() => {
    const urlParams = new URLSearchParams(window.location.search);
    const impersonationToken = urlParams.get('impersonation_token');

    if (impersonationToken) {
      // Handle impersonation login
      impersonationLogin(impersonationToken).catch((error) => {
        console.error('Failed to initialize impersonation session:', error);
        // Fall back to regular initialization
        initializeFromStorage().catch((error) => {
          console.error('Failed to initialize from storage:', error);
        });
      });
    } else {
      initializeFromStorage().catch((error) => {
        console.error('Failed to initialize from storage:', error);
      });
    }

    async function initializeFromStorage() {
      const token = localStorage.getItem('token');
      const refreshToken = localStorage.getItem('refreshToken');
      const impersonationToken = sessionStorage.getItem('impersonation_token');
      const userStr = impersonationToken ? sessionStorage.getItem('impersonation_user') : localStorage.getItem('user');

      if ((token || impersonationToken) && userStr) {
        try {
          const user = JSON.parse(userStr);

          // Validate the token by attempting to refresh it (which validates the refresh token)
          // If refresh fails, the token is invalid and we should clear the session
          if (refreshToken && !impersonationToken) {
            try {
              const { authApi } = await import('@domains/auth');
              const refreshResult = await authApi.refreshAuthToken();
              if (refreshResult.success && refreshResult.token) {
                // Token is valid, update with the refreshed token
                localStorage.setItem('token', refreshResult.token);
                import.meta.env.DEV && console.log('[AuthContext] Token validated and refreshed from localStorage');

                // Notify WebSocket service of token refresh
                import.meta.env.DEV && console.log('[AuthContext] Dispatching tokenRefreshed event');
                window.dispatchEvent(new CustomEvent('tokenRefreshed'));

                // Re-fetch fresh user data from /me to get profile_picture_url and latest permissions
                let freshUser = user;
                try {
                  const { authApi: authApiInstance } = await import('@domains/auth');
                  const meData = await authApiInstance.getMe();
                  if (meData?.id) {
                    freshUser = { ...user, ...meData };
                    localStorage.setItem('user', JSON.stringify(freshUser));
                  }
                } catch { /* use cached user if /me fails */ }

                setState(prev => ({
                  ...prev,
                  user: freshUser,
                  isAuthenticated: true,
                  isLoading: false
                }));

                // Connect WebSocket after initialization
                connectSocket();
                return;
              }
            } catch (refreshError) {
              console.warn('[AuthContext] Token validation failed, marking session as expired:', refreshError);
              markSessionExpired();
            }
          }

          // If we reach here, either no refresh token, impersonation session, or refresh failed
          // For impersonation sessions, we can't validate, so assume valid
          if (impersonationToken) {
            import.meta.env.DEV && console.log('[AuthContext] Initialized impersonation session from localStorage');

            setState({
              user,
              isAuthenticated: true,
              isLoading: false
            });
          } else {
            // No valid token, clear session
            console.warn('[AuthContext] No valid token found, clearing session');
            localStorage.removeItem('token');
            localStorage.removeItem('refreshToken');
            localStorage.removeItem('user');
            setState({
              user: null,
              isAuthenticated: false,
              isLoading: false
            });
          }
        } catch (error) {
          console.error('[AuthContext] Failed to parse user from localStorage:', error);
          localStorage.removeItem('user');
          localStorage.removeItem('impersonation_token');
          localStorage.removeItem('token');
          localStorage.removeItem('refreshToken');
          setState({
            user: null,
            isAuthenticated: false,
            isLoading: false
          });
        }
      } else {
        setState({
          user: null,
          isAuthenticated: false,
          isLoading: false
        });
      }
    }
  }, [impersonationLogin]);

  // ============================================
  // SESSION VALIDITY CHECK
  // ============================================

  useEffect(() => {
    if (!state.isAuthenticated || !state.user) return;

    const checkSession = async () => {
      try {
        const { authApi } = await import('@domains/auth');
        const result = await authApi.refreshAuthToken();
        if (!result.success) {
          import.meta.env.DEV && console.log('[AuthContext] Token refresh failed, logging out');
          await logout();
        }
      } catch (error) {
        console.error('[AuthContext] Session check failed:', error);
        // For now, don't auto-logout on session check failures
        // This prevents immediate logout after login
        console.warn('[AuthContext] Skipping auto-logout due to session check failure');
      }
    };

    // Check after 30 seconds, then every 5 minutes
    // This prevents immediate logout after login
    const timeout = setTimeout(checkSession, 30 * 1000); // 30 seconds
    const interval = setInterval(checkSession, 5 * 60 * 1000); // 5 minutes

    return () => {
      clearTimeout(timeout);
      clearInterval(interval);
    };

    return () => clearInterval(interval);
  }, [state.isAuthenticated, state.user, logout]);

  // ============================================
  // IDLE SESSION TIMEOUT (client-side signal)
  // 30 minutes of inactivity → force logout
  // The server independently enforces the same timeout via Redis.
  // ============================================

  useEffect(() => {
    if (!state.isAuthenticated) return;

    const IDLE_MS = 30 * 60 * 1000; // 30 minutes
    let idleTimer: ReturnType<typeof setTimeout>;

    const resetTimer = () => {
      clearTimeout(idleTimer);
      idleTimer = setTimeout(async () => {
        console.warn('[AuthContext] Idle timeout reached — logging out');
        await logout();
      }, IDLE_MS);
    };

    const events = ['mousemove', 'keydown', 'click', 'scroll', 'touchstart'];
    events.forEach(e => window.addEventListener(e, resetTimer, { passive: true }));
    resetTimer(); // start timer immediately

    return () => {
      clearTimeout(idleTimer);
      events.forEach(e => window.removeEventListener(e, resetTimer));
    };
  }, [state.isAuthenticated, logout]);

  // ============================================
  // LOCATION CHANGE CHECK
  // ============================================

  // Navigation session checking removed - token refresh interceptor handles expired tokens automatically

  const value: AuthContextType = {
    ...state,
    login,
    logout,
    hasPermission,
    userDomain
  };

  return (
    <AuthContext.Provider value={value}>
      {children}
    </AuthContext.Provider>
  );
};

export const useAuth = (): AuthContextType => {
  const context = useContext(AuthContext);
  if (context === undefined) {
    throw new Error('useAuth must be used within an AuthProvider');
  }
  return context;
};
